import {action, observable} from "mobx";
import Trip from "./Trip";
import TripStore from "./TripStore";
import http from "../service/http";
import moment from "moment";
import {DriveStats, ITrips, Vehicle} from "./model/Vehicle";
import * as queryString from "querystring"

export default class Trips {
    @observable all: Array<Trip>
    @observable byVehicle: Map<string, TripStore> = new Map()
    @observable byDay: Map<string, Array<Trip>>
    @observable byMonth: Map<string, Array<Trip>> = new Map()
    @observable byId: Map<string, Trip>
    @observable byDayHash: object = {}
    @observable sortedDays: Array<any>
    @observable openTrips: Array<Trip> = []
    @observable vehicle: Vehicle
    @observable loading: boolean = false


    constructor(vehicle: Vehicle) {
        this.byId = new Map()
        this.byDay = new Map()
        this.sortedDays = []
        this.all = []
        this.vehicle = vehicle
    }

    @action
    sortByDays() {
        let days = [...Object.entries(this.byDay)]
        days = days.sort((a, b) => {
            let dateA = a[0]
            let dateB = b[0]
            if (dateA < dateB) {
                return 1
            } else {
                return -1
            }
        })
        this.sortedDays = days
    }

    private addToStats(statsMap: Map<string, DriveStats>, key, stats: DriveStats) {

        let statsGroup = statsMap.get(key) || {
            distance: 0,
            distanceBusiness: 0,
            distancePrivate: 0,
            distanceWork: 0
        }

        statsGroup.distance += stats.distance
        statsGroup.distanceWork += stats.distanceWork
        statsGroup.distancePrivate += stats.distancePrivate
        statsGroup.distanceBusiness += stats.distanceBusiness
        statsMap.set(key, statsGroup)
    }

    @action
    fetch({
              vehicleId = this.vehicle._id,
              start = "2018-01-01",
              end = false,
              records = false
          }: {
        vehicleId?: string | boolean,
        start: string | boolean,
        end?: string | boolean,
        records?: boolean
    }) {
        // console.log( "FETCH  NEW TRIP DATA" )
        this.loading = true
        this.all = []
        this.byDay = new Map()
        this.byMonth = new Map()
        this.byId = new Map()
        this.byDayHash = {}

        console.info("fetching", {vehicleId, start})

        return (
            http
                .get(`/api/v2/vehicle/${vehicleId}/trips/${start}?${queryString.stringify({includeRecords: records})}`)
                .then(response => {

                    console.time("tripSetup")
                    if(!response || !response.data || !response.data.trips){
                        return
                    }

                    let all = response.data.trips.map(trip => new Trip(trip))
                    let byDay = new Map()
                    let byMonth = new Map()
                    let byId = new Map()
                    const journeys: Array<Trip> = []

                    const statsByMonth: Map<string, DriveStats> = new Map()
                    const statsByDay: Map<string, DriveStats> = new Map()
                    const statsByYear: Map<string, DriveStats> = new Map()

                    let indexDriving = 0
                    let indexParking = 0

                    const lastDriving = all.filter(trip => trip.type === 'eDriving').length - 1
                    const lastParking = all.filter(trip => trip.type === 'eParking').length - 1


                    all.forEach(trip => {
                        byId.set(trip.id, trip)

                        let year = moment(trip.day).format("YYYY")
                        let month = moment(trip.day).format("M")
                        let day = moment(trip.day).format("DD")

                        const tripStats = {
                            distance: trip.distance,
                            distanceBusiness: trip.trip_type === "eBusiness" ? trip.distance : 0,
                            distanceWork: trip.trip_type === "eWork" ? trip.distance : 0,
                            distancePrivate: trip.trip_type === "ePrivate" ? trip.distance : 0,
                        }

                        this.addToStats(statsByYear, year, tripStats)
                        this.addToStats(statsByMonth, month, tripStats)
                        this.addToStats(statsByDay, day, tripStats)

                        // console.log("->", trip.day, trip.type, {trip})
                        if (trip.type === "eDriving") {
                            journeys.push(trip)
                            trip.index = indexDriving++
                            if (trip.index === 0)
                                trip.indicator = 'first'
                            else if (trip.index === lastDriving)
                                trip.indicator = 'last'
                            else
                                trip.indicator = trip.index
                            let group = byDay.get(trip.day) || []
                            group.push(trip)
                            byDay.set(trip.day, group)

                            let monthGroup = byMonth.get(trip.month) || []
                            monthGroup.push(trip)
                            byMonth.set(trip.month, monthGroup)

                        } else {
                            trip.index = indexParking++
                            if (trip.index === 0)
                                trip.indicator = 'first'
                            else if (trip.index === lastParking)
                                trip.indicator = 'last'
                            else
                                trip.indicator = trip.index
                        }
                    })


                    this.openTrips = this.all.filter(trip => {
                        return !trip.trip_type && trip.distance > 0.5
                    })

                    this.loading = false

                    this.sortByDays()

                    let sortedDays = [...Object.entries(byDay)].sort((a, b) => {
                        let dateA = a[0]
                        let dateB = b[0]
                        if (dateA < dateB) {
                            return 1
                        } else {
                            return -1
                        }
                    })
                    this.sortedDays = sortedDays

                    const totalDistance_km = new Intl.NumberFormat("de-DE", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(all.reduce((acc, trip) => {
                        return acc + Number(trip.distance)
                    }, 0))

                    // console.log( "totalDistance_km", {totalDistance_km} )

                    const firstTrip = all.slice(0, 1)[0]
                    const centerTrips: Array<Trip> = all.slice(1, all.length - 1)
                    const lastTrip = all.slice(-1)[0]

                    const totalTime = lastTrip
                        ? moment.duration(lastTrip.startDate.diff(firstTrip.endDate))
                        : moment.duration(0)


                    let lastJourney
                    journeys.forEach(trip => {
                        if (lastJourney) {
                            lastJourney.prevTripId = trip.id
                        }
                        trip.nextTripId = lastJourney && lastJourney.id
                        lastJourney = trip
                    })

                    const trips: ITrips = {
                        all,
                        byMonth,
                        statsByDay,
                        statsByMonth,
                        statsByYear,
                        byId,
                        byDay,
                        sortedDays,
                        journeys,
                        totalTime,
                        totalDistance_km,
                        firstTrip,
                        centerTrips,
                        lastTrip
                    }
                    // console.log( "setup completed ", all.length )
                    console.timeEnd("tripSetup")
                    // console.log( "NEW TRIPS FOUND", {trips} )
                    return trips
                })
        )
    }

    @action
    fetchDrivebook({
                       vehicleId = this.vehicle._id,
                       month,
                       year,
                       records = false
                   }: {
        vehicleId?: string | boolean,
        month: string,
        year: string,
        records?: boolean
    }) {
        // console.log( "FETCH  NEW TRIP DATA" )
        this.loading = true
        this.all = []
        this.byDay = new Map()
        this.byMonth = new Map()
        this.byId = new Map()
        this.byDayHash = {}

        console.info("fetching drivebook", {vehicleId, month})

        return (
            http
                .get(`/api/v2/vehicle/${vehicleId}/drivebook/${month}?${queryString.stringify({
                    year,
                    includeRecords: records
                })}`)
                .then(response => {
                    // console.log( {response} )
                    console.time("tripSetup")

                    let all = response.data.trips.filter(t => t.distance).map(trip => new Trip(trip))
                    let byDay = new Map()
                    let byMonth = new Map()
                    let byId = new Map()
                    const journeys: Array<Trip> = []

                    const statsByMonth: Map<string, DriveStats> = new Map()
                    const statsByDay: Map<string, DriveStats> = new Map()
                    const statsByYear: Map<string, DriveStats> = new Map()

                    let indexDriving = 0
                    let indexParking = 0

                    const lastDriving = all.filter(trip => trip.type === 'eDriving').length - 1
                    const lastParking = all.filter(trip => trip.type === 'eParking').length - 1

                    all.forEach(trip => {
                        byId.set(trip.id, trip)

                        // console.log( "trip", trip.distance, trip )
                        let year = moment(trip.day).format("YYYY")
                        let month = moment(trip.day).format("M")
                        let day = moment(trip.day).format("DD")

                        const tripStats = {
                            distance: trip.distance,
                            distanceBusiness: trip.trip_type === "eBusiness" ? trip.distance : 0,
                            distanceWork: trip.trip_type === "eWork" ? trip.distance : 0,
                            distancePrivate: trip.trip_type === "ePrivate" ? trip.distance : 0,
                        }

                        this.addToStats(statsByYear, year, tripStats)
                        this.addToStats(statsByMonth, month, tripStats)
                        this.addToStats(statsByDay, day, tripStats)

                        // console.log("->", trip.day, trip.type, {trip})
                        if (trip.type === "eDriving") {
                            journeys.push(trip)
                            trip.index = indexDriving++
                            if (trip.index === 0)
                                trip.indicator = 'first'
                            else if (trip.index === lastDriving)
                                trip.indicator = 'last'
                            else
                                trip.indicator = trip.index
                            let group = byDay.get(trip.day) || []
                            group.push(trip)
                            byDay.set(trip.day, group)

                            let monthGroup = byMonth.get(trip.month) || []
                            monthGroup.push(trip)
                            byMonth.set(trip.month, monthGroup)

                        } else {
                            trip.index = indexParking++
                            if (trip.index === 0)
                                trip.indicator = 'first'
                            else if (trip.index === lastParking)
                                trip.indicator = 'last'
                            else
                                trip.indicator = trip.index
                        }
                    })


                    this.openTrips = this.all.filter(trip => {
                        return !trip.trip_type && trip.distance > 0.5
                    })

                    this.loading = false

                    this.sortByDays()

                    let sortedDays = [...Object.entries(byDay)].sort((a, b) => {
                        let dateA = a[0]
                        let dateB = b[0]
                        if (dateA < dateB) {
                            return 1
                        } else {
                            return -1
                        }
                    })
                    this.sortedDays = sortedDays

                    const totalDistance_km = new Intl.NumberFormat("de-DE", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(all.reduce((acc, trip) => {
                        return acc + Number(trip.distance || 0)
                    }, 0))

                    // console.log( "totalDistance_km", {totalDistance_km} )

                    const firstTrip = all.slice(0, 1)[0]
                    const centerTrips: Array<Trip> = all.slice(1, all.length - 1)
                    const lastTrip = all.slice(-1)[0]

                    const totalTime = lastTrip
                        ? moment.duration(lastTrip.startDate.diff(firstTrip.endDate))
                        : moment.duration(0)


                    let lastJourney
                    journeys.forEach(trip => {
                        if (lastJourney) {
                            lastJourney.prevTripId = trip.id
                        }
                        trip.nextTripId = lastJourney && lastJourney.id
                        lastJourney = trip
                    })


                    const trips: ITrips = {
                        all,
                        byMonth,
                        statsByDay,
                        statsByMonth,
                        statsByYear,
                        byId,
                        byDay,
                        sortedDays,
                        journeys,
                        totalTime,
                        totalDistance_km,
                        firstTrip,
                        centerTrips,
                        lastTrip
                    }
                    // console.log( "setup completed ", all.length )
                    console.timeEnd("tripSetup")
                    console.log("NEW TRIPS FOUND", {trips})
                    return trips
                })
        )
    }
}


// WEBPACK FOOTER //
// ./src/store/Trips.ts